import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, finalize, shareReplay, tap } from 'rxjs';
import { AuthResponse, LoginRequest, RegisterRequest, SetCoinsRequest, UserDto } from '@fraternity-wars/shared';

const API_BASE = '/api/auth';
const USERS_BASE = '/api/users';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);

  private readonly accessToken = signal<string | null>(null);
  private readonly currentUser = signal<UserDto | null>(null);
  private refreshInFlight: Observable<AuthResponse> | null = null;
  private ssoInFlight: Observable<AuthResponse> | null = null;

  readonly isAuthenticated = computed(() => this.accessToken() !== null);
  readonly user = this.currentUser.asReadonly();
  // Fraternity-Wars-scoped role, assigned centrally via admin.scuvanya.online (bereich_roles).
  readonly isAdmin = computed(() => this.currentUser()?.role === 'admin');

  getAccessToken(): string | null {
    return this.accessToken();
  }

  register(payload: RegisterRequest): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${API_BASE}/register`, payload, { withCredentials: true })
      .pipe(tap((res) => this.setSession(res)));
  }

  login(payload: LoginRequest): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${API_BASE}/login`, payload, { withCredentials: true })
      .pipe(tap((res) => this.setSession(res)));
  }

  refresh(): Observable<AuthResponse> {
    if (!this.refreshInFlight) {
      this.refreshInFlight = this.http.post<AuthResponse>(`${API_BASE}/refresh`, {}, { withCredentials: true }).pipe(
        tap((res) => this.setSession(res)),
        finalize(() => (this.refreshInFlight = null)),
        shareReplay(1),
      );
    }
    return this.refreshInFlight;
  }

  // Bridges an existing central PocketBase session (pb.scuvanya.online) into a normal
  // Fraternity Wars session. Called by authGuard when there is no valid FW session but a
  // PocketBase one exists - see core/guards/auth.guard.ts. Deduplicated the same way as
  // refresh() so concurrent guard checks (e.g. multiple routes activating at once) only
  // trigger one backend call and one refresh-token row.
  ssoLogin(pbToken: string): Observable<AuthResponse> {
    if (!this.ssoInFlight) {
      this.ssoInFlight = this.http
        .post<AuthResponse>(
          `${API_BASE}/sso`,
          {},
          { headers: { Authorization: `Bearer ${pbToken}` }, withCredentials: true },
        )
        .pipe(
          tap((res) => this.setSession(res)),
          finalize(() => (this.ssoInFlight = null)),
          shareReplay(1),
        );
    }
    return this.ssoInFlight;
  }

  logout(): Observable<void> {
    return this.http
      .post<void>(`${API_BASE}/logout`, {}, { withCredentials: true })
      .pipe(tap(() => this.clearSession()));
  }

  clearSessionLocally(): void {
    this.clearSession();
  }

  setUser(user: UserDto): void {
    this.currentUser.set(user);
  }

  setCoinBalance(balance: number): Observable<UserDto> {
    return this.http
      .patch<UserDto>(`${USERS_BASE}/me/coins`, { balance } satisfies SetCoinsRequest)
      .pipe(tap((user) => this.setUser(user)));
  }

  private setSession(res: AuthResponse): void {
    this.accessToken.set(res.accessToken);
    this.currentUser.set(res.user);
  }

  private clearSession(): void {
    this.accessToken.set(null);
    this.currentUser.set(null);
  }
}
